import { useQuery } from '@tanstack/react-query'
import { affiliatesApi } from '@/services/api/affiliates.api'

export interface AccessibleAffiliate {
  id: string
  displayName: string
  companyName?: string
  role: string
  // #1210 — Dedicated Affiliate classification. Discovery is hidden when ALL
  // accessible affiliates carry it; see useDedicatedOnlyAffiliate.
  isDedicated?: boolean
  // #1324 — raw affiliates.status and the server's "has left the platform"
  // verdict. Writes + Discovery are gated when ALL accessible affiliates have
  // left; see useAffiliateLeftPlatform.
  status?: string | null
  hasLeftPlatform?: boolean
}

/**
 * Fetches the list of affiliates the current user can access, enriched
 * with display name. Backed by GET /affiliates/mine. Pair with an
 * AffiliateMultiSelect for the global affiliate filter (#351).
 *
 * `options.enabled` lets gating callers (useDedicatedOnlyAffiliate) skip the
 * request entirely for users who can never be affiliate-only; the queryKey is
 * unchanged so the cache stays shared with the affiliate pages that already
 * warm it.
 */
export function useAccessibleAffiliates(options?: { enabled?: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: ['my-accessible-affiliates'],
    queryFn: async () => (await affiliatesApi.listMine()).data || [],
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
  return {
    affiliates: (data || []) as AccessibleAffiliate[],
    isLoading,
  }
}
